import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactCrop, { convertToPixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  centerDefaultCrop,
  cropImageToFile,
} from "../../utils/cropImage";
import "../../styles/admin/ImageCropModal.css";

const ImageCropModal = ({
  file,
  isOpen,
  queueIndex = 1,
  queueTotal = 1,
  onConfirm,
  onUseOriginal,
  onUseAllOriginals,
  onCancelAll,
}) => {
  const imgRef = useRef(null);
  const [src, setSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!file) {
      setSrc("");
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setError("");

    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const nextCrop = centerDefaultCrop(width, height);
    setCrop(nextCrop);
    setCompletedCrop(nextCrop);
  }, []);

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      setError("Selectează zona de decupat.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const pixelCrop = convertToPixelCrop(
        completedCrop,
        imgRef.current.width,
        imgRef.current.height
      );
      const cropped = await cropImageToFile(
        imgRef.current,
        pixelCrop,
        file?.name || "image.jpg"
      );

      if (!cropped) {
        setError("Nu s-a putut procesa imaginea.");
        return;
      }

      onConfirm(cropped);
    } catch {
      setError("Eroare la decupare. Încearcă din nou.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !file) return null;

  return (
    <div
      className="imgCrop_overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancelAll();
      }}
    >
      <div
        className="imgCrop_modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Decupează imaginea"
      >
        <div className="imgCrop_header">
          <div>
            <h3 className="imgCrop_title">Decupează imaginea</h3>
            <p className="imgCrop_sub">
              Trage chenarul, mută zona sau redimensionează din colțuri. Opțional: încarcă formatul original.
              {queueTotal > 1 && (
                <span className="imgCrop_queue">
                  {" "}
                  Imagine {queueIndex} din {queueTotal}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            className="imgCrop_close"
            onClick={onCancelAll}
            disabled={saving}
            aria-label="Închide"
          >
            ×
          </button>
        </div>

        <div className="imgCrop_body">
          {src && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={setCompletedCrop}
              className="imgCrop_crop"
              ruleOfThirds
              minWidth={40}
              minHeight={40}
            >
              <img
                ref={imgRef}
                src={src}
                alt="Previzualizare decupare"
                onLoad={onImageLoad}
                className="imgCrop_img"
              />
            </ReactCrop>
          )}
        </div>

        {error && <p className="imgCrop_error">{error}</p>}

        <div className="imgCrop_actions">
          <button
            type="button"
            className="imgCrop_btn imgCrop_btnGhost"
            onClick={onCancelAll}
            disabled={saving}
          >
            Anulează tot
          </button>
          <button
            type="button"
            className="imgCrop_btn imgCrop_btnGhost"
            onClick={onUseOriginal}
            disabled={saving}
          >
            Încarcă original
          </button>
          {queueTotal > 1 && (
            <button
              type="button"
              className="imgCrop_btn imgCrop_btnGhost"
              onClick={onUseAllOriginals}
              disabled={saving}
            >
              Încarcă toate originale ({queueTotal - queueIndex + 1})
            </button>
          )}
          <button
            type="button"
            className="imgCrop_btn imgCrop_btnPrimary"
            onClick={handleConfirm}
            disabled={saving || !completedCrop?.width}
          >
            {saving ? "Se procesează..." : "Confirmă decuparea"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
